import { Input } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import type { InputRef } from 'antd';
import type { ChangeEvent, FocusEvent, InputHTMLAttributes, KeyboardEvent, ReactNode, Ref, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

export function FormField({ label, htmlFor, error, hint, required, children }: { label: string; htmlFor: string; error?: string; hint?: string; required?: boolean; children: ReactNode }) {
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={htmlFor}>{label}{required ? <span aria-hidden="true"> *</span> : null}</label>
      {children}
      {error ? <div className="form-error" role="alert">{error}</div> : hint ? <div className="settings-desc">{hint}</div> : null}
    </div>
  );
}

export function FormRow({ children }: { children: ReactNode }) {
  return <div className="form-row">{children}</div>;
}

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> & { ref?: Ref<HTMLInputElement> };

/** Ant Input that still works with react-hook-form's register(): the ref resolves to the native input element. */
export function TextInput({ ref, type, onChange, onBlur, onKeyDown, ...props }: InputProps) {
  const bind = (r: InputRef | null) => {
    if (typeof ref === 'function') ref(r?.input ?? null);
    else if (ref) (ref as { current: HTMLInputElement | null }).current = r?.input ?? null;
  };
  const common = { ref: bind, onChange: onChange as (e: ChangeEvent<HTMLInputElement>) => void, onBlur: onBlur as (e: FocusEvent<HTMLInputElement>) => void, onKeyDown: onKeyDown as (e: KeyboardEvent<HTMLInputElement>) => void, className: 'form-control', ...(props as Record<string, unknown>) };
  return type === 'password' ? <Input.Password {...common} visibilityToggle={false} /> : <Input type={type} {...common} />;
}

type AreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { ref?: Ref<HTMLTextAreaElement> };

export function TextArea({ ref, rows = 4, onChange, onBlur, ...props }: AreaProps) {
  const bind = (r: TextAreaRef | null) => {
    const el = r?.resizableTextArea?.textArea ?? null;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as { current: HTMLTextAreaElement | null }).current = el;
  };
  return <Input.TextArea ref={bind} rows={rows} className="form-control" onChange={onChange as (e: ChangeEvent<HTMLTextAreaElement>) => void} onBlur={onBlur as (e: FocusEvent<HTMLTextAreaElement>) => void} {...(props as Record<string, unknown>)} />;
}

/** Native select (register()-compatible) styled with Ant tokens via .form-control. */
export function Select({ ref, options, placeholder, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { ref?: Ref<HTMLSelectElement>; options: { value: string; label: string }[]; placeholder?: string }) {
  return (
    <select ref={ref} className="form-control ant-select-native" {...props}>
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
